import { Injectable, InjectionToken, Injector } from '@angular/core';
import { parseDate, stringifyDate } from './date-serializer';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ReviverFn = (this: any, key: string, value: any) => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ReplacerFn = (this: any, key: string, value: any) => any;

/**
 * Reviver Funktion für JSON.parse(), welche alle Vorkommnisse von Strings im ISO Datetime Format in Date Objekte umwandelt.
 */
export function jsonDateReviver(this: unknown, key: string, value: unknown): unknown {
    if (typeof value === 'string') {
        return parseDate(value) ?? value;
    }
    return value;
}

/**
 * Replacer Funktion für JSON.stringify(), welche alle Vorkommnisse von Date Objekten
 * als ISO String mit Datum und Zeit in lokaler Zeitzone mit lokalem Zeitzonenoffset serialisiert.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function jsonDateReplacer(this: any, key: string, value: unknown): unknown {
    const originalValue = this && this[key];
    if (originalValue instanceof Date && !isNaN(originalValue.getTime())) {
        return stringifyDate(originalValue);
    }
    return value;
}

/**
 * Multi-Provider Token zur Registrierung von Reviver Funktionen für den JsonParser. Diese werden beim Aufruf von JsonPaser.parse() angewandt.
 */
export const JSON_REVIVERS = new InjectionToken<ReviverFn[]>('JSON_REVIVERS');

/**
 * Multi-Provider Token zur Registrierung von Replacer Funktionen für den JsonParser. Diese werden beim Aufruf von JsonPaser.stringify() angewandt.
 */
export const JSON_REPLACERS = new InjectionToken<ReplacerFn[]>('JSON_REPLACERS');

/**
 * Bietet über eigene Reviver und Replacer Funktionen - welche über Provider im Modul registriert werden können -
 * angepasste Alternativen zum nativen JSON.parse() und JSON.stringify an.
 */
@Injectable({
    providedIn: 'root',
})
export class JsonParser {
    reviverList: ReviverFn[];
    replacerList: ReplacerFn[];

    constructor(private injector: Injector) {
        this.reviverList = this.injector.get(JSON_REVIVERS, []);
        this.replacerList = this.injector.get(JSON_REPLACERS, []);
    }

    public parse(text: string, reviver?: ReviverFn): unknown {
        let reviverList = this.reviverList;
        if (reviver) {
            reviverList = [...reviverList, reviver];
        }
        return JSON.parse(text, function (key: string, value: unknown) {
            return reviverList.reduce((v, fn) => fn.call(this, key, v), value);
        });
    }

    public stringify(value: unknown, replacer?: ReplacerFn | (number | string)[] | null, space?: string | number): string {
        let replacerList = this.replacerList;
        if (Array.isArray(replacer)) {
            const allowlist = replacer.map((i) => i.toString());
            const filterReplacer = function (this: unknown, key: string, v: unknown): unknown {
                if (v !== value && typeof this === 'object' && !Array.isArray(this) && !allowlist.includes(key)) {
                    return undefined;
                }
                return v;
            };
            replacerList = [...replacerList, filterReplacer];
        } else if (replacer) {
            replacerList = [replacer, ...replacerList];
        }
        return JSON.stringify(
            value,
            function (key: string, v: unknown) {
                return replacerList.reduce((prevValue, fn) => fn.call(this, key, prevValue), v);
            },
            space,
        );
    }
}
